import { isAxiosError, AxiosResponse, AxiosError } from "axios";
import { reportApiError } from "../../../../sentry";

type Headers = Record<string, string>;

export const APIProviderFactory: IAPIProviderFactory = ({
  API,
  apiName,
  AuthService,
}) => {
  /**
   * Get default headers for specific APIs
   * Rather than having to assign the Authorization header manually each time,
   * we can do it here based on the API path.
   * @param api The API endpoint being called
   * @returns A record of headers
   */
  async function getDefaultHeaders(
    api: string
  ): Promise<Record<string, string>> {
    if (api.startsWith("/patient-centric-app-v2/")) {
      return {
        Authorization: `Bearer ${await AuthService.getAuthenticationToken()}`,
      };
    }

    return {};
  }

  const GET = async (
    api: string,
    queryStringParameters?: Record<string, any>,
    headers?: Headers,
    signal?: AbortSignal
  ) => {
    headers ??= await getDefaultHeaders(api);
    queryStringParameters ??= {};

    try {
      const apiInstance: AxiosResponse = await API.get(apiName, api, {
        headers,
        queryStringParameters,
        signal,
        response: true,
      });
      return apiInstance.data;
    } catch (error: unknown) {
      const axiosError = error as AxiosError;
      const isAxios = isAxiosError(axiosError);

      // Report all API errors to Sentry (not just authenticated ones)
      const status = isAxios ? axiosError.response?.status : undefined;
      const url = isAxios ? axiosError.config?.url : undefined;

      // Report errors with status codes (except 404) OR network errors (CORS, timeout, etc.)
      if ((status && status !== 404) || (!status && isAxios)) {
        reportApiError(
          new Error(`API GET failed: ${axiosError.message || "Unknown error"}`),
          {
            method: "GET",
            url,
            status,
            requestBody: queryStringParameters,
            responseBody: isAxios ? axiosError.response?.data : undefined,
          }
        );
      }

      if (isAxios) {
        return axiosError.response?.data;
      }
      throw new Error("Something went wrong!");
    }
  };

  const POST = async (api: string, body = {}, headers?: Headers) => {
    try {
      headers ??= await getDefaultHeaders(api);
      const apiInstance: AxiosResponse = await API.post(apiName, api, {
        headers,
        body,
        response: true,
      });
      return apiInstance.data;
    } catch (error: unknown) {
      const axiosError = error as AxiosError;
      const isAxios = isAxiosError(axiosError);

      // Report all API errors to Sentry
      const status = isAxios ? axiosError.response?.status : undefined;
      const url = isAxios ? axiosError.config?.url : undefined;

      // Report errors with status codes (except 404) OR network errors (CORS, timeout, etc.)
      if ((status && status !== 404) || (!status && isAxios)) {
        reportApiError(
          new Error(
            `API POST failed: ${axiosError.message || "Unknown error"}`
          ),
          {
            method: "POST",
            url,
            status,
            requestBody: body,
            responseBody: isAxios ? axiosError.response?.data : undefined,
          }
        );
      }

      if (isAxios) {
        return axiosError.response?.data;
      }
      throw new Error("Something went wrong!");
    }
  };

  const PUT = async (api: string, body = {}, headers?: Headers) => {
    try {
      headers ??= await getDefaultHeaders(api);
      const apiInstance: AxiosResponse = await API.put(apiName, api, {
        headers,
        body,
        response: true,
      });
      return apiInstance.data;
    } catch (error: unknown) {
      const axiosError = error as AxiosError;
      const isAxios = isAxiosError(axiosError);

      // Report all API errors to Sentry
      const status = isAxios ? axiosError.response?.status : undefined;
      const url = isAxios ? axiosError.config?.url : undefined;

      // Report errors with status codes (except 404) OR network errors (CORS, timeout, etc.)
      if ((status && status !== 404) || (!status && isAxios)) {
        reportApiError(
          new Error(`API PUT failed: ${axiosError.message || "Unknown error"}`),
          {
            method: "PUT",
            url,
            status,
            requestBody: body,
            responseBody: isAxios ? axiosError.response?.data : undefined,
          }
        );
      }

      if (isAxios) {
        return axiosError.response?.data;
      }
      throw new Error("Something went wrong!");
    }
  };

  const PATCH = async (api: string, body = {}, headers?: Headers) => {
    try {
      headers ??= await getDefaultHeaders(api);
      const apiInstance: AxiosResponse = await API.patch(apiName, api, {
        headers,
        body,
        response: true,
      });
      return apiInstance.data;
    } catch (error: unknown) {
      const axiosError = error as AxiosError;
      const isAxios = isAxiosError(axiosError);

      // Report all API errors to Sentry
      const status = isAxios ? axiosError.response?.status : undefined;
      const url = isAxios ? axiosError.config?.url : undefined;

      // Report errors with status codes (except 404) OR network errors (CORS, timeout, etc.)
      if ((status && status !== 404) || (!status && isAxios)) {
        reportApiError(
          new Error(
            `API PATCH failed: ${axiosError.message || "Unknown error"}`
          ),
          {
            method: "PATCH",
            url,
            status,
            requestBody: body,
            responseBody: isAxios ? axiosError.response?.data : undefined,
          }
        );
      }

      if (isAxios) {
        return axiosError.response?.data;
      }
      throw new Error("Something went wrong!");
    }
  };

  const DELETE = async (api: string, body = {}, headers?: Headers) => {
    try {
      headers ??= await getDefaultHeaders(api);
      const apiInstance: AxiosResponse = await API.del(apiName, api, {
        headers,
        body,
        response: true,
      });
      return apiInstance.data;
    } catch (error: unknown) {
      const axiosError = error as AxiosError;
      const isAxios = isAxiosError(axiosError);

      // Report all API errors to Sentry
      const status = isAxios ? axiosError.response?.status : undefined;
      const url = isAxios ? axiosError.config?.url : undefined;

      // Report errors with status codes (except 404) OR network errors (CORS, timeout, etc.)
      if ((status && status !== 404) || (!status && isAxios)) {
        reportApiError(
          new Error(
            `API DELETE failed: ${axiosError.message || "Unknown error"}`
          ),
          {
            method: "DELETE",
            url,
            status,
            requestBody: body,
            responseBody: isAxios ? axiosError.response?.data : undefined,
          }
        );
      }

      if (isAxios) {
        return axiosError.response?.data;
      }
      throw new Error("Something went wrong!");
    }
  };

  return {
    GET,
    POST,
    PUT,
    PATCH,
    DELETE,
  };
};
